import { defaultAddStatus, defaultAddOwnership } from "../utils/settings.js";
import { getDB } from "./schema.js";
import { pinInheritedSeen } from "../utils/seasons.js";
import { todayDate, nowIso } from "../utils/dates.js";
import { AWARDS, TIMES_FINISHED } from "../components/status-badge.js";
import { queueCloudWrite } from "./sync.js";
import { logItemChange } from "./history.js";

// Anime is HYBRID: its series → seasons → episodes STRUCTURE comes from TVmaze
// (identical to db/tv.js — one synced row per series, per-season/episode ratings +
// seen in override maps), while its rich GENRE taxonomy (genres/themes/
// demographics/studios/source) is enriched from MAL at add time and carried on the
// record. `anime_catalog` is the local-only, re-fetchable season/episode cache.

const STORE = "anime";
const CATALOG_STORE = "anime_catalog";

export const NOTE_MAX_LENGTH = 280;

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

// Override-map key for an episode. Seasons are keyed by their number as a string.
export function epKey(season, number) {
	return `${season}x${number}`;
}

// Crunchyroll-style continuous numbering. TVmaze numbers episodes per-season
// (restarting at 1 each season), but streaming services keep one running count
// across the whole series. Returns a Map of epKey(season, number) -> absolute
// number, computed over all numbered episodes in season/number order. Storage
// keys are unchanged (still season × number) — this is display-only.
export function absoluteEpisodeNumbers(episodes) {
	const ordered = (episodes ?? [])
		.filter((e) => e.season != null && e.number != null)
		.sort((a, b) => a.season - b.season || a.number - b.number);
	const map = new Map();
	ordered.forEach((e, i) => map.set(epKey(e.season, e.number), i + 1));
	return map;
}

export async function listAnime() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getAnimeById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

export async function getByTvmazeId(tvmazeId) {
	const db = await getDB();
	return db.getFromIndex(STORE, "tvmaze_id", tvmazeId);
}

export async function addAnime({
	tvmaze_id,
	title,
	premiered_year = null,
	cover_url = null,
	summary = null,
	// MAL genre enrichment (best-effort; empty when no confident title match).
	mal_id = null,
	genres = [],
	themes = [],
	demographics = [],
	studios = [],
	mal_source = null,
	engagement_status = defaultAddStatus(),
	ownership_status = defaultAddOwnership(),
}) {
	const existing = await getByTvmazeId(tvmaze_id);
	if (existing) return existing;

	const now = nowIso();
	const today = todayDate();
	const record = {
		id: uuid(),
		tvmaze_id,
		title,
		premiered_year,
		cover_url,
		summary,
		// MAL taxonomy — enriched on add, shown as chips + used by the genre filter.
		mal_id,
		genres,
		themes,
		demographics,
		studios,
		mal_source,
		media_type: "anime",
		engagement_status,
		ownership_status,
		formats: [],
		rating: null,
		awards: [],
		note: null,
		times_finished: null,
		// Per-season / per-episode user opinions. Empty = inherit from the series
		// (season inherits from series; episode inherits from season then series).
		season_overrides: {},
		episode_overrides: {},
		// Canonical column the app no longer uses (kept 0 so the cloud row is valid).
		playthrough_count: 0,
		date_first_completed: engagement_status === "done" ? today : null,
		last_engaged_date:
			engagement_status === "playing" ||
			engagement_status === "replaying" ||
			engagement_status === "done"
				? today
				: null,
		visibility: "friends",
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueCloudWrite({ type: "create", mediaType: "anime", record });
	await logItemChange("anime", null, record);
	return record;
}

export async function updateAnime(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Anime ${id} not found`);

	const next = { ...current, ...patch, updated_at: nowIso() };

	if (
		patch.engagement_status &&
		patch.engagement_status !== current.engagement_status
	) {
		const today = todayDate();
		if (
			patch.engagement_status === "playing" ||
			patch.engagement_status === "replaying"
		) {
			next.last_engaged_date = today;
		}
		if (patch.engagement_status === "done") {
			next.last_engaged_date = today;
			if (!current.date_first_completed) next.date_first_completed = today;
		}
		// Leaving "Watched": pin the inherited seen-state onto every released season
		// so what you watched survives the status change (utils/seasons.js).
		if (current.engagement_status === "done" && patch.season_overrides === undefined) {
			const catalog = await getCatalog(current.tvmaze_id);
			if (catalog) {
				next.season_overrides = pinInheritedSeen(current.season_overrides, catalog, today);
			}
		}
	}

	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: "anime", record: next });
	await logItemChange("anime", current, next);
	return next;
}

export async function setAnimeRating(id, rating) {
	if (rating !== null && (!Number.isInteger(rating) || rating < 1 || rating > 5)) {
		throw new Error("Rating must be 1-5 or null");
	}
	return updateAnime(id, { rating });
}

export async function setAnimeAwards(id, list) {
	if (!Array.isArray(list)) throw new Error("awards must be an array");
	const valid = new Set(AWARDS.map((a) => a.value));
	return updateAnime(id, { awards: list.filter((v) => valid.has(v)) });
}

export async function setAnimeNote(id, note) {
	const trimmed = (note ?? "").trim().slice(0, NOTE_MAX_LENGTH);
	return updateAnime(id, { note: trimmed === "" ? null : trimmed });
}

export async function setAnimeTimesFinished(id, value) {
	if (value !== null && !TIMES_FINISHED.some((b) => b.value === value)) {
		throw new Error(`Invalid times_finished bucket: ${value}`);
	}
	return updateAnime(id, { times_finished: value });
}

// Set/clear one field of one override entry, pruning empty entries so the maps
// stay minimal (an absent entry = "inherit").
function withOverride(map, key, field, value) {
	const next = { ...(map ?? {}) };
	const entry = { ...(next[key] ?? {}) };
	if (value === null || value === undefined) delete entry[field];
	else entry[field] = value;
	if (Object.keys(entry).length === 0) delete next[key];
	else next[key] = entry;
	return next;
}

export async function setSeasonRating(id, seasonNum, rating) {
	if (rating !== null && (!Number.isInteger(rating) || rating < 1 || rating > 5)) {
		throw new Error("Rating must be 1-5 or null");
	}
	const anime = await getAnimeById(id);
	if (!anime) throw new Error(`Anime ${id} not found`);
	let map = withOverride(anime.season_overrides, String(seasonNum), "rating", rating);
	// A rating implies you've seen it — clear any explicit "not seen" flag so the
	// rating isn't hidden by the unseen ⇒ 0 stars rule below.
	if (rating != null) map = withOverride(map, String(seasonNum), "seen", true);
	return updateAnime(id, { season_overrides: map });
}

export async function setSeasonSeen(id, seasonNum, seen) {
	const anime = await getAnimeById(id);
	if (!anime) throw new Error(`Anime ${id} not found`);
	let map = withOverride(anime.season_overrides, String(seasonNum), "seen", !!seen);
	// Unseeing drops the rating to 0 (you can't rate what you haven't seen).
	if (!seen) map = withOverride(map, String(seasonNum), "rating", null);
	return updateAnime(id, { season_overrides: map });
}

export async function setEpisodeRating(id, season, number, rating) {
	if (rating !== null && (!Number.isInteger(rating) || rating < 1 || rating > 5)) {
		throw new Error("Rating must be 1-5 or null");
	}
	const anime = await getAnimeById(id);
	if (!anime) throw new Error(`Anime ${id} not found`);
	let map = withOverride(
		anime.episode_overrides,
		epKey(season, number),
		"rating",
		rating,
	);
	// A rating implies you've seen it (see setSeasonRating).
	if (rating != null) map = withOverride(map, epKey(season, number), "seen", true);
	return updateAnime(id, { episode_overrides: map });
}

export async function setEpisodeSeen(id, season, number, seen) {
	const anime = await getAnimeById(id);
	if (!anime) throw new Error(`Anime ${id} not found`);
	let map = withOverride(
		anime.episode_overrides,
		epKey(season, number),
		"seen",
		!!seen,
	);
	// Unseeing drops the rating to 0 (see setSeasonSeen).
	if (!seen) map = withOverride(map, epKey(season, number), "rating", null);
	return updateAnime(id, { episode_overrides: map });
}

export async function deleteAnime(id) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	await db.delete(STORE, id);
	if (current) {
		// Catalog is local-only cache; drop it too (re-fetched if re-added).
		await db.delete(CATALOG_STORE, current.tvmaze_id).catch(() => {});
		await queueCloudWrite({ type: "delete", mediaType: "anime", record: current });
		await logItemChange("anime", current, null);
	}
}

export async function deleteByTvmazeId(tvmazeId) {
	const existing = await getByTvmazeId(tvmazeId);
	if (!existing) return false;
	await deleteAnime(existing.id);
	return true;
}

// --- inheritance resolvers (pure) ---
//
// Effective value = the most specific override present, else inherited:
//   episode → season → series.

// `released` (default true) gates the INHERITED default only: an unaired season
// or episode doesn't inherit the series rating / "watched" seen-state — but an
// explicit override always wins, so a future season can still be rated/marked by
// hand.
export function effectiveRating(
	anime,
	seasonNum = null,
	epNumber = null,
	{ released = true } = {},
) {
	if (seasonNum != null && epNumber != null) {
		const eo = anime.episode_overrides?.[epKey(seasonNum, epNumber)];
		// Explicitly marked not-seen ⇒ 0 stars (no rating), even if it would
		// otherwise inherit the season/series rating.
		if (eo?.seen === false) return null;
		if (eo && eo.rating != null) return eo.rating;
	}
	if (seasonNum != null) {
		const so = anime.season_overrides?.[String(seasonNum)];
		if (so?.seen === false) return null;
		if (so && so.rating != null) return so.rating;
	}
	if (!released) return null;
	return anime.rating ?? null;
}

export function effectiveSeen(
	anime,
	seasonNum = null,
	epNumber = null,
	{ released = true } = {},
) {
	if (seasonNum != null && epNumber != null) {
		const eo = anime.episode_overrides?.[epKey(seasonNum, epNumber)];
		if (eo && eo.seen != null) return eo.seen;
	}
	if (seasonNum != null) {
		const so = anime.season_overrides?.[String(seasonNum)];
		if (so && so.seen != null) return so.seen;
	}
	if (!released) return false;
	// Default: seen iff the whole entry is marked Watched.
	return anime.engagement_status === "done";
}

// True when this level (or any more-specific override) carries its own rating,
// so the UI can show an "overridden" affordance vs. the inherited default.
export function hasOwnRating(anime, seasonNum = null, epNumber = null) {
	if (seasonNum != null && epNumber != null) {
		return anime.episode_overrides?.[epKey(seasonNum, epNumber)]?.rating != null;
	}
	if (seasonNum != null) {
		return anime.season_overrides?.[String(seasonNum)]?.rating != null;
	}
	return anime.rating != null;
}

// --- catalog cache (local-only, re-fetchable; never synced) ---

export async function getCatalog(tvmazeId) {
	const db = await getDB();
	return db.get(CATALOG_STORE, tvmazeId);
}

// Local-only write — deliberately does NOT queue a cloud op (catalog is
// re-fetchable "available" metadata, not user data).
export async function saveCatalog({ tvmaze_id, seasons, episodes, status = null }) {
	const db = await getDB();
	const record = {
		tvmaze_id,
		seasons: seasons ?? [],
		episodes: episodes ?? [],
		// TVmaze show status ("Ended" → re-checked far less often; utils/seasons.js).
		status,
		fetched_at: nowIso(),
	};
	await db.put(CATALOG_STORE, record);
	return record;
}
